import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import {
  AUDIENCE_SCORE_COMPONENT_KEYS,
  type AudienceScoreComponentKey,
} from '@marketing-hub/shared';
import { Button } from '@/components/ui/Button';
import { LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import styles from '@/pages/ProjectsPage.module.css';

type ScoreComponent = {
  key: AudienceScoreComponentKey;
  score: number;
  weight: number;
  available: boolean;
  detail?: string;
};

type AudienceScore = {
  id: string;
  opportunity_score: string;
  confidence_score: string;
  components: ScoreComponent[];
  computed_at: string;
};

const COMPONENT_LABELS: Record<AudienceScoreComponentKey, string> = {
  market_size: 'Marktomvang',
  contact_completeness: 'Contactcompleetheid',
  organic_demand: 'Organische vraag (eigen site)',
  campaign_history: 'Campagne-historie',
  scraper_quality: 'Scraper-kwaliteit',
  service_fit: 'Service-fit (status)',
};

export function AudienceScoresPanel({ audienceId }: { audienceId: string }) {
  const qc = useQueryClient();

  const scoreQ = useQuery({
    queryKey: ['audience-score', audienceId],
    queryFn: () =>
      apiFetch<{ score: AudienceScore | null }>(`/api/audiences/${audienceId}/scores`),
  });

  const compute = useMutation({
    mutationFn: () =>
      apiFetch<{ score: AudienceScore }>(`/api/audiences/${audienceId}/scores/compute`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['audience-score', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audiences'] });
    },
  });

  const score = scoreQ.data?.score;
  const components = (score?.components ?? []) as ScoreComponent[];

  return (
    <section className={styles.card}>
      <div className={styles.row}>
        <h2 style={{ margin: 0 }}>Scores</h2>
        <Button
          type="button"
          variant="secondary"
          onClick={() => compute.mutate()}
          disabled={compute.isPending}
        >
          Herberekenen
        </Button>
      </div>
      <p className={styles.mutedSmall}>
        Deterministische scores (geen LLM). Gewichten volgens{' '}
        {AUDIENCE_SCORE_COMPONENT_KEYS.length} componenten in ADR-003.
      </p>

      {scoreQ.isLoading ? <LoadingState /> : null}

      {!scoreQ.isLoading && !score ? (
        <StateMessage title="Nog geen score">
          Klik op Herberekenen om opportunity en confidence te berekenen uit CRM-, integratie- en
          scraper-data.
        </StateMessage>
      ) : null}

      {score ? (
        <>
          <div className={styles.row} style={{ gap: 'var(--space-lg)', marginTop: 'var(--space-md)' }}>
            <div>
              <p className={styles.mutedSmall}>Opportunity</p>
              <p style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>
                {Number(score.opportunity_score).toFixed(0)}
              </p>
            </div>
            <div>
              <p className={styles.mutedSmall}>Confidence</p>
              <p style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>
                {Number(score.confidence_score).toFixed(0)}
              </p>
            </div>
            <p className={styles.mutedSmall} style={{ alignSelf: 'flex-end' }}>
              Berekend {new Date(score.computed_at).toLocaleString('nl-NL')}
            </p>
          </div>

          <h3 style={{ marginTop: 'var(--space-lg)' }}>Componenten</h3>
          <ul className={styles.list}>
            {components.map((c) => (
              <li key={c.key}>
                <div className={styles.row}>
                  <span>
                    {COMPONENT_LABELS[c.key] ?? c.key}
                    {!c.available ? (
                      <span className={styles.mutedSmall}> · geen data</span>
                    ) : null}
                  </span>
                  <strong>{c.available ? c.score.toFixed(0) : '—'}</strong>
                </div>
                {c.available ? (
                  <div
                    style={{
                      height: 6,
                      background: 'var(--color-border, #e5e7eb)',
                      borderRadius: 3,
                      marginTop: 4,
                    }}
                  >
                    <div
                      style={{
                        width: `${c.score}%`,
                        height: '100%',
                        background: 'var(--color-signal, #0f766e)',
                        borderRadius: 3,
                      }}
                    />
                  </div>
                ) : null}
                {c.detail ? <p className={styles.mutedSmall}>{c.detail}</p> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
